import { AlertDialog } from "radix-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { loginPath } from "@/auth/guards";
import { useAuth } from "@/auth/use-auth";
import { Button } from "@/components/ui/button";
import { flushDrafts } from "@/lib/forms/draftStore";

/**
 * "Time is up — please sign in again" (T11-02): the page underneath stays as it is; drafts are
 * flushed to IndexedDB first, and after signing in the staff member comes back to this page
 * (`/login?next=`), where the form offers to restore the draft.
 */
export function SessionExpiredDialog({ next }: { next: string }) {
  const { t } = useTranslation("auth");
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const goSignIn = async () => {
    await flushDrafts();
    await signOut();
    void navigate(loginPath(next), { replace: true });
  };
  return (
    <AlertDialog.Root open>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-[90] bg-black/50" />
        <AlertDialog.Content
          data-slot="session-expired"
          className="fixed top-1/2 left-1/2 z-[91] flex w-[min(28rem,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-lg bg-background p-5 shadow-lg"
          onEscapeKeyDown={(event) => {
            event.preventDefault();
          }}
        >
          <AlertDialog.Title className="text-lg font-bold">{t("expired.title")}</AlertDialog.Title>
          <AlertDialog.Description className="text-sm">{t("expired.body")}</AlertDialog.Description>
          <AlertDialog.Action asChild>
            <Button size="lg" onClick={() => void goSignIn()}>
              {t("expired.sign_in")}
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
